/**
 * HR แก้ไข/เพิ่ม/ลบเวลาเข้า-ออกของพนักงานหนึ่งวัน — ฟอร์มที่เปิดคาไว้ใต้แถววันนั้น
 * ในหน้า "ประวัติเข้างานรายคน" (โจทย์สุกรีเภสัช: ขอแก้เวลาเองได้เหมือน HumanSoft).
 *
 * ⚠️ บังคับกรอกเหตุผลทุกครั้ง — เวลาที่ HR พิมพ์เองไม่มีรูป/พิกัดเป็นหลักฐาน
 * เหตุผลคือสิ่งเดียวที่เหลือให้ตรวจย้อนหลังตอนพนักงานทักว่าเงินไม่ตรง.
 * ลบเวลารอบไหน = ล้างช่องนั้นให้ว่างแล้วกดบันทึก.
 */
import { useState } from 'react';
import { useEditAttendance, type MyAttendanceDay } from '../../hooks';
import { useToast } from '../ui/toast';

const KINDS = [
  { key: 'morning_in', label: 'เข้างาน' },
  { key: 'lunch_out', label: 'ออกพัก' },
  { key: 'lunch_in', label: 'เข้าจากพัก' },
  { key: 'evening_out', label: 'เลิกงาน' },
] as const;

type Kind = (typeof KINDS)[number]['key'];

/** ISO timestamp → "HH:mm" ตามเวลาไทย (ค่าเริ่มต้นในช่องกรอก) */
function toBkkHHmm(iso: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Bangkok',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}

export function AttendanceDayEditor({
  empCode,
  day,
  onDone,
}: {
  empCode: string;
  day: MyAttendanceDay;
  onDone: () => void;
}) {
  const edit = useEditAttendance();
  const toast = useToast();
  const initial = Object.fromEntries(
    KINDS.map((k) => [k.key, day.events[k.key] ? toBkkHHmm(day.events[k.key]!.at) : '']),
  ) as Record<Kind, string>;
  const [times, setTimes] = useState<Record<Kind, string>>(initial);
  const [reason, setReason] = useState('');

  const changedKinds = KINDS.filter((k) => times[k.key] !== initial[k.key]);

  async function save() {
    if (changedKinds.length === 0) {
      toast.error('ยังไม่ได้แก้เวลาช่องไหนเลย');
      return;
    }
    if (reason.trim().length < 3) {
      toast.error('ต้องระบุเหตุผลที่แก้ (อย่างน้อย 3 ตัวอักษร)');
      return;
    }
    try {
      await edit.mutateAsync({
        emp_code: empCode,
        date: day.date,
        entries: changedKinds.map((k) => ({ kind: k.key, time: times[k.key] || null })),
        reason: reason.trim(),
      });
      toast.success('บันทึกเวลาแล้ว');
      onDone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3">
      <div className="grid grid-cols-2 gap-2">
        {KINDS.map((k) => (
          <div key={k.key}>
            <label className="label">{k.label}</label>
            <input
              type="time"
              className="input"
              value={times[k.key]}
              onChange={(e) => setTimes({ ...times, [k.key]: e.target.value })}
            />
          </div>
        ))}
      </div>
      <p className="text-[11px] text-gray-500">ล้างช่องให้ว่าง = ลบเวลารอบนั้นทิ้ง</p>

      <div>
        <label className="label">เหตุผลที่แก้ (บันทึกไว้ในประวัติการใช้งานระบบ)</label>
        <input
          type="text"
          className="input"
          placeholder="เช่น ลืมสแกนออก แจ้งหัวหน้าแล้ว / เครื่องสแกนเสีย"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={300}
        />
      </div>

      <div className="flex gap-2">
        <button type="button" onClick={onDone} className="btn-secondary flex-1 text-sm">
          ยกเลิก
        </button>
        <button
          type="button"
          disabled={edit.isPending}
          onClick={save}
          className="btn-primary flex-1 text-sm"
        >
          {edit.isPending ? 'กำลังบันทึก…' : `บันทึก${changedKinds.length > 0 ? ` (${changedKinds.length} รอบ)` : ''}`}
        </button>
      </div>
    </div>
  );
}
